import { Injectable } from '@angular/core';
import { ScaledImage, ScaledImageSize } from '@cf-shared/data-model/scaled-image';
import { ImageSize } from '@cf-shared/enums';
import { ImageProps } from 'data-model/image';
import { AuthService } from './auth.service';
import * as crypto from 'crypto';
import { DebugService } from './debug.service';
import { CloudFunctionsService } from './cloud-functions.service';
import { LoggingService } from './logging.service';
import { ConnectionState, OnlineStateService } from './online-state.service';
import { buildImageVariantKey, ImageVariantSize, planImageVariants, putImageToSignedUrl, upscaleToShorterEdge } from './image-upload-helpers';
import { computeStorageOwnerTokenSync } from './storage-owner-token';
import { buildMarkdownImageTag, buildPublicDownloadUrl, derivePathFromPublicUrl } from './image-url-helpers';
import { ImageUploadOutboxService } from './image-upload-outbox.service';
import { NativeImageCacheService } from './native-image-cache.service';
import { densityForWidth, pickDisplayVariant } from './native-image-cache-rules';
import { isOfflineOrTransientNetworkError } from './error-patterns';
import { AppEnvironment } from 'environments/environment';
import { isCapacitorNative } from 'environments/environment-helpers';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { describeError } from '@cf-shared/error-describe';
import {
  imageDecodeFailedMessage,
  imageReadFailedMessage,
  imageTooSmallMessage,
  imageUploadFailedMessage,
} from './image-upload-messages';

@Injectable()
export class ImageService {
  // when the last "photo queued" toast was shown, in epoch ms (debug clock so
  // E2E fakeDate stays consistent). multi-size uploads enqueue up to 4 size
  // variants of ONE photo in a burst — show one toast for the whole burst.
  private queuedToastShownAt_ms = 0;

  constructor(
    private auth: AuthService,
    protected debug: DebugService,
    private cloudFunctions: CloudFunctionsService,
    private logging: LoggingService,
    private outbox: ImageUploadOutboxService,
    private toastCtrl: ToastController,
    private nativeImageCache: NativeImageCacheService,
    private onlineState: OnlineStateService,
  ) { }

  public static generateImageId(imageName: string, userId: string, timestamp_utc: string): string {
    let matchResult = imageName.match(/(.+)\.([0-9a-z]+)(?:[\?#]|$)/i);
    if (!matchResult) {
      return crypto.createHash('sha256').update(imageName).digest('hex');
    }

    let fileName = matchResult[1];
    let fileExtension = matchResult[2];
    let imageId = `${fileName}-${timestamp_utc}-${userId}`;
    let imageIdHashedHex = crypto.createHash('sha256').update(imageId).digest('hex');
    return `${imageIdHashedHex}.${fileExtension}`;
  }

  private isThumbnail(size: { width: number; height: number }) {
    return size.width === ImageSize.Thumbnail && size.height === ImageSize.Thumbnail;
  }

  private getImageDpi(imageWidth: number) {
    return `${densityForWidth(imageWidth)}x`;
  }

  // #3989 follow-up (queued offline photos must display immediately): the URL
  // a display helper hands out for a size still queued in the offline outbox
  // cannot load (the bytes have not reached the server yet), so substitute the
  // outbox's local object URL while one exists. After the drain the map entry
  // disappears and the next change-detection pass returns the remote URL
  // again. Legacy sizes without a `path` (pre-outbox documents) always use
  // their stored url.
  // #5692: next, on the native apps, a copy the offline image cache holds on
  // the device. On the web that cache is off and the service worker does the job.
  private getDisplayUrl(size: ScaledImageSize): string {
    if (size.path) {
      const localPreviewUrl = this.outbox.getLocalPreviewUrl(size.path);
      if (localPreviewUrl) {
        return localPreviewUrl;
      }
    }
    return this.nativeImageCache.localUrlFor(size.url) ?? size.url;
  }

  /** An <img> could not load `failedSrc`. When that was a file of the native
   * image cache (#5692), the cache forgets it and this answers the remote URL
   * to show instead; the error handlers then leave the image alone. Otherwise
   * undefined. */
  forgetLocalImageCopy(failedSrc: string | undefined | null): string | undefined {
    return this.nativeImageCache.forgetLocalCopy(failedSrc);
  }

  /** Whether this (typically just-failed) <img> URL points at an object key
   * that is still queued in the offline outbox. Used by the image error
   * handlers so they do not delete the image reference of a photo whose bytes
   * are safely on this device. */
  async isQueuedImageUrlAsync(url: string): Promise<boolean> {
    const path = derivePathFromPublicUrl(AppEnvironment.storageBucket, url);
    if (!path) {
      return false;
    }
    try {
      return await this.outbox.hasQueuedAsync(path);
    } catch (err) {
      // an outbox DB failure here must not crash an <img> error handler —
      // report it and treat the image as not queued (the pre-outbox behavior)
      this.logging.trackSilentProblem('ImageOutboxQueuedLookupFailed', {
        name: err instanceof Error ? err.name : undefined,
        message: describeError(err),
      });
      return false;
    }
  }

  getImageThumbnailUrl(image: ScaledImage) {
    if (image && image.sizes) {
      const thumbnail = image.sizes.find(size => size.width === ImageSize.Thumbnail && size.height === ImageSize.Thumbnail);
      if (thumbnail) {
        // #5692: keep the card's thumbnail for offline use on the native apps
        this.nativeImageCache.request(thumbnail.url);
        return this.getDisplayUrl(thumbnail);
      }
    }

    return '';
  }

  getSrcSetByDpi(image: ScaledImage) {
    // #5692: on the native apps the offline cache keeps ONE larger size, the
    // one this screen's density would pick. once it is on the device (or queued
    // in the outbox), show just that; until then the full remote list below
    const variant = this.nativeImageCache.isEnabled ? pickDisplayVariant(image.sizes, window.devicePixelRatio || 1) : undefined;
    if (variant) {
      this.nativeImageCache.request(variant.url);
      const displayUrl = this.getDisplayUrl(variant);
      if (displayUrl !== variant.url) {
        return [`${displayUrl} 1x`];
      }
    }

    const srcSet: string[] = [];
    if (image.sizes) {
      image.sizes.forEach(size => {
        if (!this.isThumbnail(size)) {
          srcSet.push(`${this.getDisplayUrl(size)} ${this.getImageDpi(size.width)}`);
        }
      });
    }

    // Special handling when thumbnail needs to be server instead of a widescreen image
    if (!srcSet.length) {
      srcSet.push(`${this.getImageThumbnailUrl(image)} 256w`);
    }

    return srcSet;
  }

  getSrcSetBySize(image: ScaledImage) {
    if (image.sizes) {
      return image.sizes.map(size => `${this.getDisplayUrl(size)} ${size.width}w`);
    }

    return null;
  }

  // the markdown reference an editor inserts for a just-uploaded image, or ''
  // (reported to Sentry) when the image has no storage object key
  getMarkdownTag(image: ScaledImage) {
    const tag = buildMarkdownImageTag(AppEnvironment.storageBucket, image?.sizes);
    // if the uploaded image has no usable object key, insert nothing rather
    // than a reference the renderer cannot show, and report it
    if (!tag) {
      this.logging.trackSilentProblem('ImageMarkdownTagNoObjectKey', {
        sizeCount: image?.sizes?.length ?? 0,
      });
      return '';
    }
    return tag;
  }

  processUploadImages(
    images: FileList,
    collectionName: string,
    callbackSuccess: (scaledImage?: ScaledImage) => void,
    callbackFail: (message?: TranslatedString) => void,
    createMultipleSizes: boolean,
    // Optional override for the minimum accepted shorter-edge size (in px).
    // When omitted, the default depends on createMultipleSizes (see processImage).
    // Used by the coach-branding logo uploader, where a small logo is fine.
    minImageSize?: number,
  ) {
    // no file selected is a no-op success (an empty FileList must not leave the
    // caller's loading spinner hanging with neither callback fired).
    if (!images || images.length === 0) {
      callbackSuccess();
      return;
    }

    for (let i = 0; i < images.length; i++) {
      const imageName = `${images[i].name}`;
      const imageNameHashed = `${collectionName}/${ImageService.generateImageId(
        imageName,
        this.auth.user.uid,
        this.debug.getNowLocal().toUTC().toISOTime(),
      )}`;

      // read the file as a data URL, then resize + upload it
      const reader = new FileReader();
      reader.onload = () => this.processImage(reader.result as string, imageNameHashed, callbackSuccess, callbackFail, createMultipleSizes, minImageSize);
      // if the read fails, surface it instead of leaving the caller hanging
      reader.onerror = () => callbackFail(imageReadFailedMessage());
      if (images[i]) {
        reader.readAsDataURL(images[i]);
      }
    }
  }

  private processImage(
    image: string,
    fileName: string,
    callbackSuccess: (scaledImage: ScaledImage) => void,
    callbackFail: (message?: TranslatedString) => void,
    createMultipleSizes: boolean,
    minImageSize?: number,
  ) {
    let newImage = new Image();
    newImage.onload = () => {
      newImage.id = fileName;
      const imageHeight = newImage.height;
      const imageWidth = newImage.width;
      // ask the pure planner which minimum applies to this source and whether
      // the source clears it. only this file-picker path refuses a small
      // source; processDataUrlAsync (the label and recipe scans) scales a small
      // one up instead.
      const { minShorterEdge, sourceTooSmall } = planImageVariants({
        sourceWidth: imageWidth,
        sourceHeight: imageHeight,
        multipleSizes: createMultipleSizes,
        minShorterEdge: minImageSize,
      });
      if (sourceTooSmall) {
        callbackFail(imageTooSmallMessage(minShorterEdge));
        return;
      }
      // run the upload in the background. use the two-argument then() so an
      // upload failure routes to callbackFail, while a throw from
      // callbackSuccess itself does NOT also trigger callbackFail — the two
      // handlers are mutually exclusive, so there is no double callback.
      // the user is shown one translated message rather than the raw exception
      // text, which was untranslatable and meaningless to them (#4366). the
      // error still reaches Sentry: createCanvasAndUploadAsync reports each of
      // its failure paths through reportUploadFailureOnce.
      this.createCanvasAndUploadAsync(newImage, createMultipleSizes).then(
        callbackSuccess,
        () => callbackFail(imageUploadFailedMessage()),
      );
    };

    // if the image data fails to decode, surface it instead of leaving the
    // caller (e.g. a loading spinner) hanging with neither callback fired.
    newImage.onerror = () => callbackFail(imageDecodeFailedMessage());
    newImage.src = image;
  }

  private async createCanvasAndUploadAsync(
    image: HTMLImageElement,
    createMultipleSizes: boolean,
    // whether this upload reports its own failures to Sentry. true for the
    // generic uploader paths, whose failure handlers don't report (e.g.
    // edit-exercise's only dismisses the loader); false when the caller already
    // reports a feature-specific event (the label-scan path in edit-food), so
    // we don't double-report one failure.
    reportFailures: boolean = true,
    // when set, a source whose shorter side is under this many pixels is
    // uploaded scaled up to it instead of producing no size at all. only the
    // scan path (processDataUrlAsync) passes it — see upscaleToShorterEdge.
    upscaleToMinShorterEdge?: number,
  ): Promise<ScaledImage> {
    // the canvas draw below only uses the source's shape, so planning against
    // a scaled-up size is all it takes for the draw to scale the picture up
    const { width: imageWidth, height: imageHeight } = upscaleToMinShorterEdge
      ? upscaleToShorterEdge(image.width, image.height, upscaleToMinShorterEdge)
      : { width: image.width, height: image.height };

    const uploads: Promise<ScaledImageSize>[] = [];
    // report a failure of this whole produce-and-upload operation to Sentry
    // only once. "upload" in the event name is historical: the canvas-context,
    // blob-encode and no-sizes-produced failures below route here too, and they
    // reach no other reporter (#4366). the size variants run
    // concurrently via Promise.all, so a failure that hits every variant
    // (e.g. a CORS-blocked PUT, #3707) would otherwise report up to four times
    // for one photo. the first failure reports; later ones are swallowed here
    // (Promise.all still rejects with the first error, so the user-facing
    // failure path is unaffected). use trackSilentProblem, not
    // trackAbnormalException: the uploader already shows the user an alert on
    // failure, so a Sentry-only report avoids a second user-facing toast.
    let uploadFailureReported = false;
    const reportUploadFailureOnce = (err: unknown) => {
      // if the caller reports its own failures, skip the generic report
      if (!reportFailures || uploadFailureReported) {
        return;
      }
      uploadFailureReported = true;
      this.logging.trackSilentProblem('Image upload failed', {
        name: err instanceof Error ? err.name : undefined,
        message: describeError(err),
      });
    };
    // which sizes to produce, and how big each canvas is. the whole rule lives
    // in the pure planner so it can be unit-tested without a browser — see
    // planImageVariants in image-upload-helpers.ts. no minimum is passed: this
    // method has never refused a small source. the label and recipe scans reach
    // it through processDataUrlAsync, which scales a small source up instead
    // (upscaleToMinShorterEdge above), so they always get at least one size.
    const { variants } = planImageVariants({
      sourceWidth: imageWidth,
      sourceHeight: imageHeight,
      multipleSizes: createMultipleSizes,
    });

    variants.forEach((variant: ImageVariantSize) => {
      const size = variant.width;
      uploads.push(
        new Promise<ScaledImageSize>((resolve: (value: ScaledImageSize) => void, reject: (reason?: unknown) => void) => {
          // the storage path for this size variant (used in the error below and the upload)
          const imagePath = this.getImagePath(image.id, size);

          // create an empty canvas element and get its 2d context
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          // a 2d context can be null (out of memory / canvas too large); reject
          // so the failure surfaces instead of a null deref later at drawImage.
          if (!ctx) {
            const err = new Error(`Could not get 2d canvas context (path: ${imagePath})`);
            reportUploadFailureOnce(err);
            reject(err);
            return;
          }

          canvas.width = variant.width;
          canvas.height = variant.height;

          const imageAspectRatio = imageWidth / imageHeight;
          const canvasAspectRatio = canvas.width / canvas.height;
          let renderHeight: number, renderWidth: number, dx: number, dy: number;

          // Fit on width, and center accordingly.
          if (imageAspectRatio > canvasAspectRatio) {
            renderHeight = canvas.height;
            renderWidth = imageWidth * (renderHeight / imageHeight);
            dx = (canvas.width - renderWidth) / 2;
            dy = 0;
          } else if (imageAspectRatio < canvasAspectRatio) {
            renderWidth = canvas.width;
            renderHeight = imageHeight * (renderWidth / imageWidth);
            dx = 0;
            dy = (canvas.height - renderHeight) / 2;
          }

          // Happy path - keep aspect ratio
          else {
            renderHeight = canvas.height;
            renderWidth = canvas.width;
            dx = 0;
            dy = 0;
          }

          ctx.drawImage(image, dx, dy, renderWidth, renderHeight);

          // get a blob version of the canvas; default is png, so explicitly ask for jpeg
          const imageProps = {
            path: imagePath,
            width: canvas.width,
            height: canvas.height,
          } as ImageProps;

          canvas.toBlob(
            (blob: Blob | null) => {
              // toBlob yields null if the canvas could not be encoded
              if (!blob) {
                const err = new Error(`Failed to encode image to blob (path: ${imageProps.path})`);
                reportUploadFailureOnce(err);
                reject(err);
                return;
              }
              imageProps.file = blob;
              // propagate the upload's success/failure to this promise. doing the
              // upload inside an async executor instead would swallow a failure as
              // an unhandled rejection and leave Promise.all pending forever.
              this.uploadImage(imageProps, reportUploadFailureOnce).then(resolve, reject);
            },
            'image/jpeg',
            0.7,
          );
        }),
      );
    });

    // Promise.all rejects if any size fails to upload, so every entry here is a
    // resolved ScaledImageSize — no falsy entries to filter out.
    const sizes = await Promise.all(uploads);
    // if the source was too small for every ladder size, no variant was
    // uploaded; surface that instead of returning an empty ScaledImage that
    // would later crash on read (e.g. sizes[sizes.length - 1].url).
    if (sizes.length === 0) {
      const err = new Error(`No image sizes produced (source too small) for ${image.id}`);
      reportUploadFailureOnce(err);
      throw err;
    }
    return { sizes };
  }

  /** Runs the online check after a transient PUT failure (df-28f906a7558b). A failure of the
   *  check itself is reported, never allowed to lose the photo the caller is about to queue. */
  private async recheckConnectionAfterPutFailureAsync(): Promise<void> {
    try {
      await this.onlineState.verifyOnlineStatus(ConnectionState.Offline, 'suspected');
    } catch (checkErr) {
      this.logging.trackSilentProblem('ImageUploadConnectionRecheckFailed', { error: describeError(checkErr) });
    }
  }

  private async uploadImage(image: ImageProps, onUploadError: (err: unknown) => void): Promise<ScaledImageSize> {
    // whether the failure below came from the PUT, which the global fetch wrapper no longer
    // online-checks (it is marked as caller-reported), rather than from the signed-URL call
    let putStarted = false;
    try {
      // get a signed upload URL from the server
      const { signedUrl } = await this.cloudFunctions.storageGetUploadUrlAsync({
        path: image.path,
        contentType: 'image/jpeg',
      });

      // upload directly to GCS. wrap the canvas blob in a file so capacitor's
      // native http bridge serializes the binary body correctly. the bridge only
      // base64-encodes a body that is `instanceof File`; a plain blob falls
      // through and is mis-sent as json. on ios that put throws
      // (CapacitorUrlRequestError "error 0" -> Sentry 3Z0); on android it silently
      // uploads the 2-byte string "{}" instead of the image. a file fixes both.
      const uploadFile = new File([image.file], 'image.jpg', { type: 'image/jpeg' });
      // PUT the blob to GCS. Pass a no-op error hook here: the helper would
      // report BEFORE the catch below can classify the failure, and an expected
      // offline failure (the enqueue path) must not report at all. The catch
      // calls onUploadError itself for every non-transient failure, so an HTTP
      // error or an unrecognised rejection still reaches the dev team and
      // surfaces to the user. A CORS-blocked PUT (issue #3707) rejects like a
      // connection blip, so it is queued and reported only once the outbox gives
      // up on it (design-docs/telemetry.md, "Global Fetch Wrapper").
      // onUploadError dedups across size variants (see the caller).
      putStarted = true;
      await putImageToSignedUrl(signedUrl, uploadFile, image.path, () => { /* classified in the catch below */ });
    } catch (err) {
      // offline / transient network failure: queue for later instead of losing
      // the photo (#3989, umbrella F4). the final URL is deterministic (public
      // object URL), so the caller can save its document now; the outbox drain
      // uploads the bytes when the connection returns. non-transient failures
      // keep the loud path below. no Sentry report on the enqueue path — being
      // offline is expected, not a problem; volume goes to Mixpanel instead
      // (image_outbox_enqueued). the combined predicate matters: the signed-URL
      // call throws the wrapped CloudFunctionNetworkError form, the raw PUT
      // throws bare fetch errors — either one means "queue it".
      if (isOfflineOrTransientNetworkError(err)) {
        // if the PUT failed, check the connection as the fetch wrapper did before the PUT was
        // marked: a real outage moves the app to Offline, so the outbox drains when it is back
        if (putStarted) {
          await this.recheckConnectionAfterPutFailureAsync();
        }
        try {
          await this.outbox.enqueueAsync(image.path, image.file, 'image/jpeg');
          await this.precacheQueuedImageAsync(image);
        } catch (enqueueErr) {
          // enqueueAsync failed, so the photo is lost after all. report the
          // ENQUEUE error, not the network one above: being offline is expected
          // and deliberately unreported, while a broken outbox (IndexedDB quota
          // exhausted) is the actual problem to debug. without this the user
          // sees only the generic "couldn't upload" message and nothing reaches
          // Sentry at all (#4366). precacheQueuedImageAsync is inside the try
          // only as belt-and-braces — it swallows its own errors by design,
          // because a missing local preview does not lose the queued bytes.
          onUploadError(enqueueErr);
          throw enqueueErr;
        }
        this.showQueuedToast();
        const url = buildPublicDownloadUrl(AppEnvironment.storageBucket, image.path);
        return { url, width: image.width, height: image.height, path: image.path };
      }
      onUploadError(err);
      throw err;
    }

    // the bytes are on GCS now — only the canonical download-URL lookup is
    // left, so this deliberately sits OUTSIDE the enqueue try/catch: a
    // transient failure here must NOT re-enqueue an already-uploaded photo
    // (wrong toast + wrong image_outbox_enqueued volume).
    try {
      const { downloadUrl } = await this.cloudFunctions.storageGetDownloadUrlAsync({ path: image.path });
      return { url: downloadUrl, width: image.width, height: image.height, path: image.path };
    } catch (err) {
      // expected: transient network failure from the cloud-function call after
      // the PUT succeeded. the client-built URL is byte-identical to what the
      // server would return (pinned by image-url-helpers.test.ts), so fall
      // back to it — no enqueue, no toast, nothing to report.
      if (isOfflineOrTransientNetworkError(err)) {
        console.log('ImageService: download-URL lookup failed after successful PUT, using client-built URL', image.path);
        return { url: buildPublicDownloadUrl(AppEnvironment.storageBucket, image.path), width: image.width, height: image.height, path: image.path };
      }
      onUploadError(err);
      throw err;
    }
  }

  /** On web/PWA, put the queued bytes into the SW 'images' cache under the
   * final URL so this device renders the photo before the upload completes.
   * Native has no SW; the image stays pending there until the drain runs
   * (accepted for this phase — Phase 3's UX work adds pending indicators). */
  private async precacheQueuedImageAsync(image: ImageProps): Promise<void> {
    // native has no service worker; older/locked-down web contexts may lack
    // the Cache API entirely
    if (isCapacitorNative() || !('caches' in window)) {
      return;
    }
    try {
      // a raw cache.put bypasses the ExpirationPlugin bookkeeping, but only
      // until the SW route first serves this URL: workbox's
      // cachedResponseWillBeUsed hook calls updateTimestamp() on every served
      // read (workbox-expiration/ExpirationPlugin.js), which adopts the entry
      // into the 300-entry/90-day tracking. only a photo the user never views
      // again stays untracked — bounded by outbox volume, accepted.
      const cache = await caches.open('images');
      await cache.put(
        buildPublicDownloadUrl(AppEnvironment.storageBucket, image.path),
        new Response(image.file, { headers: { 'Content-Type': 'image/jpeg' } }),
      );
    } catch (err) {
      // expected: QuotaExceededError / SecurityError from the Cache API in
      // storage-pressured or hardened/private-mode browsers. cache-put is a
      // best-effort nicety: the queued upload itself is already safe in the
      // outbox (and telemetered via image_outbox_enqueued), so a failure here
      // only costs the instant local preview — log-and-continue by design
      console.log('ImageService: queued-image precache failed', image.path, err);
    }
  }

  /** Show the "photo saved on this device" toast, at most once per 5s burst
   * of size-variant enqueues (one photo = up to 4 enqueued variants). */
  private showQueuedToast(): void {
    const now_ms = this.debug.getNowLocal().toMillis();
    // if a toast for this burst is already up, don't stack another
    if (now_ms - this.queuedToastShownAt_ms < 5000) {
      return;
    }
    this.queuedToastShownAt_ms = now_ms;
    this.toastCtrl.showToast({
      uniqueId: 'image-queued-offline-toast',
      message: LL.common.messages.imageQueuedOffline(),
      type: ToastType.WarningLight,
      duration: 5000,
      position: 'top',
    });
  }

  async processDataUrlAsync(dataUrl: string, collectionName: string, createMultipleSizes: boolean): Promise<ScaledImage> {
    const imageNameHashed = `${collectionName}/${ImageService.generateImageId(
      'label-scan.jpg',
      this.auth.user.uid,
      this.debug.getNowLocal().toUTC().toISOTime(),
    )}`;

    return new Promise<ScaledImage>((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        img.id = imageNameHashed;
        try {
          // pass reportFailures: false — both callers (edit-food's label scan
          // and its recipe scan) report their own 'LabelScanImageUploadFailed' /
          // 'RecipeScanImageProcessFailed' event, so a generic report here would
          // double-report the same failure. a picture smaller than Small is
          // scaled up rather than producing no size at all (df-5fbd3836e3a3).
          resolve(await this.createCanvasAndUploadAsync(img, createMultipleSizes, false, ImageSize.Small));
        } catch (err) {
          // name the source size in the error, so the callers' reports show
          // whether a picture's size had anything to do with the failure
          reject(new Error(`${describeError(err)} (scan picture ${img.width}x${img.height})`, { cause: err }));
        }
      };
      img.onerror = () => reject(new Error('Failed to load image from data URL'));
      img.src = dataUrl;
    });
  }

  public async deleteImageAsync(path: string) {
    // if the bytes are still queued in the offline outbox, the server never
    // received them — cancelling the queued upload IS the deletion
    if (await this.outbox.removeQueuedAsync(path)) {
      return;
    }
    // A key minted before owner tokens existed carries no owner, so the server
    // cannot confirm it is ours and answers the delete as a recorded no-op
    // rather than an error (#5016). The call is still made, deliberately: the
    // server's log is then the one place the volume of those skips is visible,
    // and it costs nothing here because the answer does not throw. The document
    // reference is dropped by the caller either way, so the user still sees the
    // picture gone.
    await this.cloudFunctions.storageDeleteAsync({ path });
  }

  /** Delete every size variant of a photo, best-effort (#3989 follow-up 2):
   * the user's REMOVE action must never be blocked by the network. Queued
   * variants are cancelled locally; uploaded variants get a server delete
   * that may fail — the caller proceeds either way (the object stays
   * orphaned in the bucket, the same end state as any pre-existing delete
   * failure). Offline failures are expected and logged only; anything else
   * is reported to telemetry. */
  public async deleteImageSizesAsync(sizes: ScaledImageSize[]): Promise<void> {
    try {
      await Promise.all(sizes.map(size => this.deleteImageAsync(size.path)));
    } catch (err) {
      // expected: offline/transient network failure from storageDeleteAsync —
      // same philosophy as the upload paths in this file: being offline is
      // not a problem worth a Sentry report
      if (isOfflineOrTransientNetworkError(err)) {
        console.log('ImageService: delete skipped while offline, object stays orphaned', err);
        return;
      }
      const errWithCode = err as { code?: string };
      this.logging.trackSilentProblem('ImageDeleteFailed', {
        code: errWithCode?.code,
        name: err instanceof Error ? err.name : undefined,
        message: describeError(err),
      });
    }
  }

  // Build the GCS object key for one size variant. Delegates to the Angular-free
  // helper so the key-building rule stays unit-testable (see image-upload-helpers).
  // The owner token is what lets the server later confirm this key is ours — a
  // key minted without one can never be deleted again (#5016).
  private getImagePath(fileName: string, size?: number) {
    return buildImageVariantKey(fileName, computeStorageOwnerTokenSync(this.auth.user.uid), size);
  }
}
